import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ServerStackIcon,
  CircleStackIcon,
  PresentationChartLineIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { fetchManagedTenants } from '../api/client';
import SummaryTab from './slo/SummaryTab';
import CollectionTab from './slo/CollectionTab';
import SilverStagingTab from './slo/SilverStagingTab';
import IngestTab from './slo/IngestTab';
import MirrorTab from './slo/MirrorTab';

const TABS = [
  { id: 'summary',        label: 'Summary',           sub: 'End-to-end pipeline time',         icon: PresentationChartLineIcon },
  { id: 'collection',     label: 'Collection',         sub: 'M365 → bronze-staging',            icon: ChartBarIcon },
  { id: 'silver-staging', label: 'Bronze → Silver',    sub: 'bronze-staging → silver-staging',  icon: ServerStackIcon },
  { id: 'ingest',         label: 'Silver → Delta',     sub: 'silver-staging → unified Delta',   icon: CircleStackIcon },
  { id: 'mirror',         label: 'Delta → Mirror',     sub: 'unified Delta → ATS event mirror', icon: TableCellsIcon },
];

const STORAGE_KEY = 'm365signal-slo-active-tab';

function getInitialTab() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (TABS.some((t) => t.id === saved)) return saved;
  } catch {
    /* ignore */
  }
  return 'summary';
}

export default function SloDashboard() {
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [tenantId, setTenantId] = useState('');
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchManagedTenants()
      .then((rows) => {
        if (cancelled) return;
        setTenants(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const tenantLookup = React.useMemo(() => {
    const map = {};
    for (const t of tenants) {
      const id = t.tenantId || t.tenantID;
      const name = t.shortName || t.friendlyName || t.domain || id;
      if (id) map[id] = name;
    }
    return map;
  }, [tenants]);

  const handleTabClick = (id) => {
    setActiveTab(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  };

  const selectedLabel = !tenantId
    ? 'All tenants'
    : (tenantLookup[tenantId] || tenantId);

  return (
    <div className="p-6 max-w-full">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100">Pipeline Health</h1>
        <p className="text-sm text-gray-400 mt-1">
          Per-stage SLO status from data ingestion through to Delta append. Admin only.
        </p>
      </header>

      {/* Tenant filter */}
      <div className="mb-5 flex items-center gap-3 flex-wrap">
        <label className="text-xs text-gray-400">Tenant filter</label>
        <select
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          className="input-field text-sm"
          style={{ minWidth: 240 }}
        >
          <option value="">All tenants</option>
          {tenants
            .map((t) => {
              const id = t.tenantId || t.tenantID;
              const shortName = t.shortName;
              const friendlyName = t.friendlyName;
              const label = shortName && friendlyName
                ? `(${shortName}) ${friendlyName}`
                : (shortName || friendlyName || t.domain || id);
              return { id, label };
            })
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
            .map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <span className="text-[11px] text-gray-500">
          Selection: <span className="text-gray-300 font-medium">{selectedLabel}</span> · applies across all tabs
        </span>
      </div>

      {/* Tabs */}
      <nav className="flex gap-1 border-b border-gray-800 mb-6 flex-wrap">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                isActive
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>
                {tab.label}
                <span className="block text-[10px] text-gray-500 font-normal -mt-0.5">{tab.sub}</span>
              </span>
            </button>
          );
        })}
      </nav>

      {/* Tabs render lazily but stay mounted so polling continues only on the active tab */}
      <div hidden={activeTab !== 'summary'}>
        <SummaryTab active={activeTab === 'summary'} tenantId={tenantId} tenantLookup={tenantLookup} />
      </div>
      <div hidden={activeTab !== 'collection'}>
        <CollectionTab active={activeTab === 'collection'} tenantId={tenantId} tenantLookup={tenantLookup} />
      </div>
      <div hidden={activeTab !== 'silver-staging'}>
        <SilverStagingTab active={activeTab === 'silver-staging'} tenantId={tenantId} tenantLookup={tenantLookup} />
      </div>
      <div hidden={activeTab !== 'ingest'}>
        <IngestTab active={activeTab === 'ingest'} tenantId={tenantId} tenantLookup={tenantLookup} />
      </div>
      {/* Mirror completeness has no per-tenant grain upstream; the tab says so rather
          than silently ignoring the dashboard-wide tenant filter. */}
      <div hidden={activeTab !== 'mirror'}>
        <MirrorTab active={activeTab === 'mirror'} tenantId={tenantId} />
      </div>
    </div>
  );
}
